import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressRing from './ProgressRing';
import CreateEventModal from './CreateEventModal';
import EventCalendar from './EventCalendar';
import './admin-dashboard.css';

const {
  FiCalendar,
  FiCheckSquare,
  FiChevronRight,
  FiEdit3,
  FiFilter,
  FiPlus,
  FiSearch,
  FiSend,
  FiSquare,
  FiTrash2,
  FiUsers,
  FiX
} = FiIcons;

function parseLocalDate(value){
  const match=String(value||'').match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if(match)return new Date(Number(match[1]),Number(match[2])-1,Number(match[3]));
  const date=new Date(value);
  return Number.isNaN(date.getTime())?null:new Date(date.getFullYear(),date.getMonth(),date.getDate());
}

function formatDate(value){
  const date=parseLocalDate(value);
  return date?`${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}-${String(date.getFullYear()).slice(-2)}`:value||'—';
}

function paymentBadge(event){
  const status=String(event.wooStatus||'');
  const value=status.toLowerCase();
  if(value.includes('completed')||value.includes('event done'))return{label:'Event Completed',className:'completed'};
  if(value.includes('paid')||value.includes('full'))return{label:'Paid in Full',className:'paid'};
  if(value.includes('balance'))return{label:'Balance Due',className:'balance'};
  if(value.includes('deposit'))return{label:'Deposit Due',className:'deposit'};
  return status?{label:status,className:'balance'}:null;
}

function cloneSections(sections) {
  return Array.isArray(sections) ? JSON.parse(JSON.stringify(sections)) : [];
}

function AdminDashboard({ events, openEvent, onDeleteEvents, onEventCreated, monthOnly = false }) {
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('Upcoming');
  const [view, setView] = useState('list');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [thisMonth, setThisMonth] = useState(monthOnly);
  const today=new Date();
  today.setHours(0,0,0,0);

  const filtered = useMemo(() => [...events].filter((event) => {
    const matchesQuery = `${event.name} ${event.client} ${event.venue}`
      .toLowerCase()
      .includes(query.toLowerCase());
    const date=parseLocalDate(event.date);
    if(thisMonth){
      const inMonth=date&&date.getFullYear()===today.getFullYear()&&date.getMonth()===today.getMonth();
      return matchesQuery&&Boolean(inMonth);
    }
    const isPast=date&&date<today;
    const needsAction=Number(event.completion||0)<100&&
      event.status!=='Approved'&&event.status!=='Completed';
    const matchesStatus=status==='Past'
      ?Boolean(isPast)
      :status==='Needs action'
        ?!isPast&&needsAction
        :!isPast;
    return matchesQuery&&matchesStatus;
  }).sort((first,second)=>
    (parseLocalDate(first.date)?.getTime()??Number.MAX_SAFE_INTEGER)-
    (parseLocalDate(second.date)?.getTime()??Number.MAX_SAFE_INTEGER)
  ), [events, query, status, thisMonth]);

  const selectedEvents = events.filter((event) =>
    selectedIds.some((id) => String(id) === String(event.id))
  );
  const allVisibleSelected = filtered.length > 0 && filtered.every((event) =>
    selectedIds.some((id) => String(id) === String(event.id))
  );

  const createEvent = (form, editQuestionnaire = false, questionnaireTemplate = null) => {
    const item = {
      ...form,
      id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      date: form.date || 'Date pending',
      completion: 0,
      updated: 'Just now',
      status: 'Not Started',
      guests: form.guests || '—',
      dj: form.dj || 'TBD',
      services: Array.isArray(form.services) ? [...form.services] : [],
      serviceDetails: form.serviceDetails || '',
      questionnaireTemplateId: questionnaireTemplate?.id || '',
      questionnaireSections: cloneSections(questionnaireTemplate?.sections)
    };

    onEventCreated?.(item, editQuestionnaire, questionnaireTemplate);
  };

  const toggleSelection = (eventId) => {
    const normalizedId = String(eventId);
    setSelectedIds((current) => current.some((id) => String(id) === normalizedId)
      ? current.filter((id) => String(id) !== normalizedId)
      : [...current, eventId]);
  };

  const toggleVisibleSelection = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) =>
        !filtered.some((event) => String(event.id) === String(id))
      ));
      return;
    }

    setSelectedIds((current) => [
      ...new Set([...current, ...filtered.map((event) => event.id)])
    ]);
  };

  const deleteEvents = async () => {
    if (selectedEvents.length === 0 || deleting) return;

    const eventLabel = selectedEvents.length === 1
      ? `"${selectedEvents[0].name}"`
      : `${selectedEvents.length} events`;

    if (!window.confirm(`Delete ${eventLabel}? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      await onDeleteEvents?.(selectedEvents.map((event) => event.id));
      setSelectedIds([]);
    } catch (error) {
      window.alert(error.message || 'The event could not be deleted. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const editSelected = () => {
    if (selectedEvents.length === 1) openEvent(selectedEvents[0]);
  };

  return (
    <main className="main-content events-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Event management</span>
          <h1>Events</h1>
          <p>Create, search, edit, and manage every booked event.</p>
        </div>
        <button type="button" className="button primary" onClick={() => setShowCreate(true)}>
          <SafeIcon icon={FiPlus} /> Create event
        </button>
      </div>

      <section className="events-view-bar">
        <div className="event-filter-chips">
          {thisMonth&&<button type="button" className="active" onClick={()=>setThisMonth(false)}>This month ✕</button>}
          {['Upcoming','Needs action','Past'].map((item)=><button
            type="button"
            key={item}
            className={!thisMonth&&status===item?'active':''}
            onClick={()=>{setThisMonth(false);setStatus(item);}}
          >{item}</button>)}
        </div>
        <div className="event-view-switch">
          <button type="button" className={view==='list'?'active':''} onClick={()=>setView('list')}>List</button>
          <button type="button" className={view==='calendar'?'active':''} onClick={()=>setView('calendar')}>Calendar</button>
        </div>
      </section>

      {view==='calendar'
        ?<EventCalendar events={filtered}/>
        :<section className="panel">
          <div className="panel-header">
            <div><h2>All events</h2><p>Search and manage booked event records.</p></div>
            <div className="toolbar">
              <label className="search">
                <SafeIcon icon={FiSearch} />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search events" aria-label="Search events" />
              </label>
              <label className="select-wrap">
                <SafeIcon icon={FiFilter} />
                <select value={status} onChange={(event) => {setThisMonth(false);setStatus(event.target.value);}} aria-label="Filter events">
                  <option>Upcoming</option><option>Needs action</option><option>Past</option>
                </select>
              </label>
            </div>
          </div>

          <section className="metric-grid">
            <article><SafeIcon icon={FiCalendar} /><span><strong>{events.length}</strong><small>Total events</small></span></article>
            <article><SafeIcon icon={FiUsers} /><span><strong>{events.filter((event) => event.status === 'In Progress').length}</strong><small>Clients filling in</small></span></article>
            <article><SafeIcon icon={FiSend} /><span><strong>{events.filter((event) => event.status === 'Under Review').length}</strong><small>Answers to review</small></span></article>
          </section>

          <div className={`selection-toolbar ${selectedIds.length ? 'visible' : ''}`}>
            <div className="selection-summary">
              <SafeIcon icon={FiCheckSquare} />
              <strong>{selectedIds.length} selected</strong>
              <span>{selectedIds.length === 1 ? 'Choose an action for this event.' : 'Delete selected events in one action.'}</span>
            </div>
            <div className="selection-actions">
              <button type="button" className="button secondary" disabled={selectedIds.length !== 1} onClick={editSelected}>
                <SafeIcon icon={FiEdit3} /> Edit event
              </button>
              <button type="button" className="button danger-button" disabled={deleting} onClick={deleteEvents}>
                <SafeIcon icon={FiTrash2} /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" className="icon-btn" onClick={() => setSelectedIds([])} aria-label="Clear selected events">
                <SafeIcon icon={FiX} />
              </button>
            </div>
          </div>

          <div className="event-list">
            <div className="event-list-select-all">
              <label>
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleSelection} aria-label="Select all visible events" />
                <span>Select visible events</span>
              </label>
            </div>
            {filtered.length === 0 ? (
              <div className="events-empty"><strong>No events found</strong><span>Try changing your search or status filter.</span></div>
            ) : filtered.map((event, index) => {
              const date=parseLocalDate(event.date);
              const days=date?Math.round((date.getTime()-today.getTime())/86400000):null;
              const payment=paymentBadge(event);
              const selected=selectedIds.some((id)=>String(id)===String(event.id));
              return (
                <motion.div
                  className="event-row"
                  key={event.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${event.name}`}
                  onClick={() => openEvent(event)}
                  onKeyDown={(keyEvent) => {
                    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                      keyEvent.preventDefault();
                      openEvent(event);
                    }
                  }}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <label className="event-select-checkbox" onClick={(eventClick) => eventClick.stopPropagation()}>
                    <input type="checkbox" checked={selected} onChange={() => toggleSelection(event.id)} aria-label={`Select ${event.name}`} />
                    <span className="checkbox-icon"><SafeIcon icon={selected ? FiCheckSquare : FiSquare} /></span>
                  </label>
                  <div className="event-avatar">{event.name.charAt(0)}</div>
                  <div className="event-primary"><strong>{event.name}</strong><span>{event.client} · {formatDate(event.date)}</span></div>
                  <div className="event-meta"><small>VENUE · DJ</small><span>{event.venue||'Venue pending'} · {event.dj||'TBD'}</span></div>
                  <div className="event-meta"><small>UNTIL EVENT</small><span>{days===null?'Date pending':days<0?`${Math.abs(days)} days ago`:days===0?'Today':`${days} days`}</span></div>
                  <div className="completion"><ProgressRing value={event.completion} /><span><small>{event.completion||0}% · {event.status}</small>{payment&&<small className={`payment-badge ${payment.className}`}>{payment.label}</small>}{event.wooOrderNumber&&<small>Order #{event.wooOrderNumber}</small>}</span></div>
                  <SafeIcon icon={FiChevronRight} className="row-arrow" />
                </motion.div>
              );
            })}
          </div>
        </section>}

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreate={createEvent}
        />
      )}
    </main>
  );
}

export default AdminDashboard;